import React from 'react';
import FsSingleBarGraph from 'ui-elements/charts/FsSingleBarGraph/FsSingleBarGraph';
import { Box, Fade } from '@material-ui/core';
import FsPaper from 'ui-elements/main-elements/FsPaper';
import FsProsConsInfoBox from 'ui-elements/boxes/FsProsConsInfoBox';
import { FsComponentSize } from 'globals/types';
import useTheme from '@material-ui/core/styles/useTheme';
import { green } from 'globals/colors';
import reportLocales from 'globals/locales/report';
import { observer } from 'mobx-react';
import { useReportContext } from 'contexts/report/ReportContext';
import { isSuccessState } from 'globals/utils';
import FsDefaultFallbackComponent from 'ui-elements/loaders/FsDefaultFallbackComponent';
import { getHelperTooltipContent } from 'ui-elements/modals/FsDefaultDialog/FsDefaultDialog.utils';
import {
  Caption,
  ContainerBox,
  Paragraph,
} from './FinancialCredibility.style-elements';
import {
  getFirstBoxStyles,
  getSingleBarGraphStyle,
} from './FinancialCredibility.style';

// Locales
const {
  title,
  prosConsCaption,
  descriptionPrefix,
  tooltipInfo,
} = reportLocales.financialCredibility;

const FinancialCredibility: React.FC = () => {
  const {
    report: {
      financialCredibility: { status, barGraph, cons, pros },
    },
  } = useReportContext();
  const description = `${descriptionPrefix} ${barGraph?.maxValue}`;
  const theme = useTheme();
  const singleBarGraphStyle = getSingleBarGraphStyle(theme);
  const firstBoxStyles = getFirstBoxStyles(theme);

  if (!isSuccessState(status))
    return (
      <FsDefaultFallbackComponent
        status={status}
        data-test="default-fallback-component"
        withMargin={false}
      />
    );
  return (
    <Fade in={isSuccessState(status)} timeout={1000}>
      <FsPaper
        title={title}
        tooltipContent={getHelperTooltipContent(tooltipInfo)}
        data-test="financial-credibility-component"
      >
        <ContainerBox>
          <FsSingleBarGraph
            style={singleBarGraphStyle}
            value={barGraph?.value}
            valueSize={FsComponentSize.L}
            barWidth={46}
            barHeight={200}
            barColor={green.MAIN}
            barValue={barGraph?.barValue()}
            description={description}
            descriptionSize={FsComponentSize.L}
            wrapWords={false}
            isLastElement={false}
          />
          <Box flexGrow="1">
            <Caption>{prosConsCaption}</Caption>
            <FsProsConsInfoBox type="cons" style={firstBoxStyles}>
              {cons().map((con) => (
                <Paragraph key={con} data-test="single-con">
                  {con}
                </Paragraph>
              ))}
            </FsProsConsInfoBox>

            <FsProsConsInfoBox type="pros">
              {pros().map((pro) => (
                <Paragraph key={pro} data-test="single-prop">
                  {pro}
                </Paragraph>
              ))}
            </FsProsConsInfoBox>
          </Box>
        </ContainerBox>
      </FsPaper>
    </Fade>
  );
};

FinancialCredibility.displayName = 'FinancialCredibility';

export default observer(FinancialCredibility);
